import React, { useState, useEffect } from 'react';
import { Building2, Plus, FileText, AlertCircle, Calendar, FolderOpen, Edit, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import CompanyDocumentModal from '../components/CompanyDocumentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categorie documenti HSE Aziendali
const COMPANY_HSE_CATEGORIES = {
  'cciaa_visura': '1. CCIAA e Visura ordinaria',
  'durc': '2. DURC',
  'doma': '3. DOMA',
  'dvr': '4. DVR',
  'polizza_rct': '5. Polizza RCT e quietanza',
  'autocertificazione_ccnl': '6. Autocertificazione contratto collettivo',
  'corso_nomina_rls': '7. Corso e Nomina RLS',
  'corso_nomina_rspp': '8. Corso e Nomina RSPP',
  'nomina_mc': '9. Nomina MC',
  'dichiarazioni_art14': '10. Dichiarazione art.14 e altre dichiarazioni',
  'idoneita_mezzi': '11. Dichiarazione idoneità mezzi e attrezzature',
  'anga_raee': '12. Anga_Raee',
  'ultimo_bilancio': '13. Ultimo Bilancio',
  'gestione_infortuni': '14. Gestione Infortuni',
  'certificazioni_iso': '15. Certificazioni ISO 2025',
  'estintori': '5. Estintori',
  'cassette_ps': '6. Cassette PS',
  'attrezzi': '7. Attrezzi',
  'mezzi_tecnici': '8. Mezzi Tecnici',
  'piano_emergenza': 'PIANO DI EMERGENZA',
  'piano_miglioramento': 'PIANO DI MIGLIORAMENTO',
  'policy_hse': 'Policy HSE',
  'procedure_hse': 'Procedure HSE'
};

function HSEAziendale() {
  const [documents, setDocuments] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, expired: 0, expiring_30: 0 });
  const [currentUser, setCurrentUser] = useState(null);
  const [editingCompliance, setEditingCompliance] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
    fetchData();
  }, []);

  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  // Delete compliance item
  const deleteComplianceItem = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento e tutti gli allegati?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/compliance/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setCompliance(compliance.filter(c => c._id !== itemId));
        alert('Documento eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting compliance item:', err);
      alert('Errore di connessione');
    }
  };

  // Delete document attachment
  const deleteDocument = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setDocuments(documents.filter(d => d._id !== docId));
        alert('Allegato eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Errore di connessione');
    }
  };

  // Open edit modal
  const openEditCompliance = (item) => {
    setEditingCompliance(item);
    setEditForm({
      titolo: item.titolo || '',
      descrizione: item.descrizione || '',
      categoria: item.categoria || '',
      data_rilascio: item.data_rilascio ? new Date(item.data_rilascio).toISOString().split('T')[0] : '',
      data_scadenza: item.data_scadenza ? new Date(item.data_scadenza).toISOString().split('T')[0] : '',
      numero_protocollo: item.numero_protocollo || ''
    });
  };

  // Update compliance item
  const updateComplianceItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const payload = {
      ...editForm,
      data_rilascio: editForm.data_rilascio ? new Date(editForm.data_rilascio).toISOString() : null,
      data_scadenza: editForm.data_scadenza ? new Date(editForm.data_scadenza).toISOString() : null
    };
    
    try {
      const res = await fetch(`${API_URL}/api/compliance/${editingCompliance._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setCompliance(compliance.map(c => c._id === updated._id ? updated : c));
        setEditingCompliance(null);
        alert('Documento aggiornato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (err) {
      console.error('Error updating compliance item:', err);
      alert('Errore di connessione');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch company compliance items
      const compRes = await fetch(`${API_URL}/api/compliance?employee_id=company_hse`);
      const compData = await compRes.json();
      setCompliance(compData);

      // Fetch company documents
      const docsRes = await fetch(`${API_URL}/api/documents?entity_type=company`);
      const docsData = await docsRes.json();
      setDocuments(docsData);

      // Calculate stats
      const now = new Date();
      const expired = compData.filter(c => c.days_remaining < 0).length;
      const expiring_30 = compData.filter(c => c.days_remaining >= 0 && c.days_remaining <= 30).length;

      setStats({
        total: compData.length,
        expired,
        expiring_30
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching company HSE data:', err);
      setLoading(false);
    }
  };

  const getUrgencyBadgeClass = (level) => {
    if (level === 'expired' || level === 'critical') return 'badge-critical';
    if (level === 'warning') return 'badge-warning';
    if (level === 'soon') return 'badge-soon';
    return 'badge-ok';
  };

  const getCategoryLabel = (cat) => {
    return COMPANY_HSE_CATEGORIES[cat] || cat;
  };

  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento HSE Aziendale...</p>
      </div>
    );
  }

  return (
    <div data-testid="hse-aziendale-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Building2 size={32} className="text-primary" />
              HSE Aziendale
            </h1>
            <p className="page-description">
              Gestione documentazione HSE a livello aziendale
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
            data-testid="add-document-button"
          >
            <Plus size={20} />
            Carica Documento
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Documenti Totali</p>
              <p className="text-3xl font-bold">{stats.total}</p>
            </div>
            <FileText size={32} className="text-primary" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-orange-50 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Scadenze 30gg</p>
              <p className="text-3xl font-bold text-orange-600">{stats.expiring_30}</p>
            </div>
            <AlertCircle size={32} className="text-orange-600" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Scaduti</p>
              <p className="text-3xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <AlertCircle size={32} className="text-red-600" />
          </div>
        </div>
      </div>

      {/* Documents by Category */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-6">Documenti per Categoria</h2>

        {compliance.length === 0 ? (
          <div className="empty-state">
            <FileText className="empty-state-icon" />
            <p>Nessun documento aziendale caricato</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => setShowModal(true)}
            >
              <Plus size={20} />
              Carica Primo Documento
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(
              compliance.reduce((acc, item) => {
                const cat = item.categoria || 'altro';
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
              }, {})
            ).map(([categoria, items]) => (
              <div key={categoria} className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted px-4 py-3 font-semibold flex items-center gap-2">
                  <FolderOpen size={20} className="text-primary" />
                  {getCategoryLabel(categoria)}
                  <span className="ml-auto badge badge-secondary">{items.length}</span>
                </div>
                <div className="divide-y divide-border">
                  {items.map((item) => {
                    const itemDocs = documents.filter(d => d.entity_id === item._id);
                    return (
                      <div
                        key={item._id}
                        className="p-4 hover:bg-muted/30 transition-colors"
                        data-testid="document-item"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold">{item.titolo}</h4>
                              {item.days_remaining !== null ? (
                                <span className={`badge ${getUrgencyBadgeClass(item.urgency_level)}`}>
                                  {item.days_remaining >= 0
                                    ? `${item.days_remaining} giorni`
                                    : 'Scaduto'}
                                </span>
                              ) : (
                                <span className="badge badge-secondary">Nessuna scadenza</span>
                              )}
                              {/* Edit/Delete buttons */}
                              {canManage && (
                                <div className="flex gap-1 ml-auto">
                                  <button
                                    className="btn btn-secondary p-1.5"
                                    onClick={() => openEditCompliance(item)}
                                    title="Modifica"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    className="btn btn-secondary p-1.5 text-destructive"
                                    onClick={() => deleteComplianceItem(item._id)}
                                    title="Elimina"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                            {item.descrizione && (
                              <p className="text-sm text-muted-foreground mb-2">
                                {item.descrizione}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                              {item.data_rilascio && (
                                <span>
                                  📅 Inizio:{' '}
                                  {format(new Date(item.data_rilascio), 'dd/MM/yyyy', {
                                    locale: it,
                                  })}
                                </span>
                              )}
                              {item.data_scadenza && (
                                <span>
                                  ⏰ Scadenza:{' '}
                                  {format(new Date(item.data_scadenza), 'dd/MM/yyyy', {
                                    locale: it,
                                  })}
                                </span>
                              )}
                              {item.numero_protocollo && (
                                <span className="font-mono">#{item.numero_protocollo}</span>
                              )}
                            </div>
                            {itemDocs.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {itemDocs.map((doc) => (
                                  <div key={doc._id} className="inline-flex items-center gap-1 bg-primary/10 rounded-lg overflow-hidden">
                                    <a
                                      href={`${API_URL}/api/download/${doc._id}`}
                                      className="inline-flex items-center gap-2 px-3 py-1 text-primary text-sm hover:bg-primary/20 transition-colors"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid="download-attachment"
                                    >
                                      <FileText size={16} />
                                      {doc.nome_file}
                                      <span className="text-xs">
                                        ({(doc.dimensione / 1024).toFixed(0)} KB)
                                      </span>
                                    </a>
                                    {canManage && (
                                      <button
                                        className="px-2 py-1 text-destructive hover:bg-destructive/20 transition-colors"
                                        onClick={() => deleteDocument(doc._id)}
                                        title="Elimina allegato"
                                      >
                                        <X size={14} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CompanyDocumentModal
          onClose={(shouldRefresh) => {
            setShowModal(false);
            if (shouldRefresh) {
              fetchData();
            }
          }}
        />
      )}

      {/* Edit Compliance Modal */}
      {editingCompliance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Modifica Documento HSE</h2>
              <button onClick={() => setEditingCompliance(null)} className="btn btn-secondary p-2">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={updateComplianceItem} className="p-6 space-y-4">
              <div className="form-group">
                <label className="form-label">Titolo *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.titolo}
                  onChange={(e) => setEditForm({...editForm, titolo: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrizione</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={editForm.descrizione}
                  onChange={(e) => setEditForm({...editForm, descrizione: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Data Rilascio</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editForm.data_rilascio}
                    onChange={(e) => setEditForm({...editForm, data_rilascio: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Data Scadenza</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editForm.data_scadenza}
                    onChange={(e) => setEditForm({...editForm, data_scadenza: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select
                  className="form-input"
                  value={editForm.categoria}
                  onChange={(e) => setEditForm({...editForm, categoria: e.target.value})}
                >
                  {Object.entries(COMPANY_HSE_CATEGORIES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">N. Protocollo</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.numero_protocollo}
                  onChange={(e) => setEditForm({...editForm, numero_protocollo: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingCompliance(null)}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  Salva Modifiche
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HSEAziendale;
